import { message, Select, Upload } from 'antd';
import { UploadChangeParam, UploadFile } from 'antd/es/upload';
import { t } from 'i18next';
import { useEffect, useMemo, useState } from 'react';
import { ICustomTabs } from 'src/app/components/TabLayoutComponent/tab-layout.config';
import TabLayout from 'src/app/components/TabLayoutComponent/TabLayout';
import TableComponent, { CustomColumnsType } from 'src/app/components/TableComponent/TableComponent';
import TooltipWithIcon from 'src/app/components/TooltipWithIconComponent/TooltipWithIcon';
import { axios } from 'src/app/utils';
import { urlsConfig } from 'src/configs';
import ImportIcon from 'src/icons/ImportIcon';

const exampleData = [
  {
    currency: 'US Dollar',
    country: 'United States',
    code: 'USD',
    annual: [25, 25, 25, 25, 25],
    quarterly: [25, 25, 25, 25, 25],
    monthly: [25, 25, 25, 25, 25],
  },
  {
    currency: 'Euro',
    country: 'Eurozone',
    code: 'EUR',
    annual: [25, 25, 25, null, 25],
    quarterly: [25, 25, 25, 25, 25],
    monthly: [25, 25, 25, 25, 25],
  },
];
interface CurrencyLookup {
  id: string;
  name: string;
  symbol: string;
  currency: string;
  countryOrType: string;
  annual: string;
  quarterly: string;
  monthly: string;
  createdAt: string;
  updatedAt: string;
}

interface HeaderLookup {
  id: string;
  name: string;
  headers: string[];
  createdAt: string;
  updatedAt: string;
}

interface CurrencyTable {
  id: string;
  name: string;
  symbol: string;
  currency: string;
  countryOrType: string;
  annual: any[];
  quarterly: any[];
  monthly: any[];
  createdAt: string;
  updatedAt: string;
}

interface HeaderTable {
  id: string;
  name: string;
  headers: string[];
  createdAt: string;
  updatedAt: string;
}

interface TableLookup {
  currencies: CurrencyLookup[];
  headers: HeaderLookup[];
}

type RateType = 'annual' | 'quarterly' | 'monthly';

export default function ExchangeRates() {
  const [rateType, setRateType] = useState<RateType>('annual');

  const [currencies, setCurrencies] = useState<CurrencyTable[]>([]);
  const [headers, setHeaders] = useState<HeaderTable[]>([]);

  useEffect(() => {
    getTableData();
  }, []);

  const handleFileImport = async (value: UploadChangeParam<UploadFile<any>>) => {
    const importFile = value.fileList[0].originFileObj;

    const formData = new FormData();

    if (!importFile) {
      message.error('File could not be found...');
      return;
    }

    formData.append('file', importFile);

    await axios.post(urlsConfig.importCurrency, formData);
    await getTableData();
  };

  async function getTableData() {
    const { data }: { data: TableLookup } = await axios.get(urlsConfig.currencies.table);
    if (!data) return;

    setCurrencies(
      data.currencies.map((prev) => ({
        ...prev,
        annual: JSON.parse(prev.annual),
        monthly: JSON.parse(prev.monthly),
        quarterly: JSON.parse(prev.quarterly),
      })),
    );
    setHeaders(data.headers);
  }

  const selectedHeader = useMemo(() => {
    const headerName = rateType === 'annual' ? 'Annual' : rateType === 'monthly' ? 'Monthly' : 'Quarterly';
    return headers.find((header) => header.name === headerName);
  }, [headers, rateType]);

  const newColumns: CustomColumnsType<CurrencyTable> = useMemo(() => {
    const fixedColumns: CustomColumnsType<CurrencyTable> = [
      {
        title: 'Name',
        dataIndex: 'name',
        key: 'name',
        width: '85px',
      },
      {
        title: 'Symbol',
        dataIndex: 'symbol',
        key: 'symbol',
        width: '85px',
      },
      {
        title: 'Currency',
        dataIndex: 'currency',
        key: 'currency',
        width: '150px',
      },
      {
        title: 'Country / Type',
        dataIndex: 'countryOrType',
        key: 'countryOrType',
        width: '150px',
      },
      {
        title: 'Current',
        dataIndex: 'current',
        key: 'current',
        width: '150px',
      },
    ];

    const dynamicColumns: CustomColumnsType<CurrencyTable> =
      selectedHeader?.headers.map((header, index) => ({
        title: header,
        key: `${rateType}-${index}`,
        width: '100px',
        render: (_, record) => {
          return record[rateType]?.[index] ?? '-';
        },
      })) ?? [];
    return [...fixedColumns, ...dynamicColumns];
  }, [selectedHeader, rateType]);

  const createImportBtn = () => {
    return (
      <div style={{ display: 'flex', gap: '2em' }}>
        <Upload
          beforeUpload={() => false}
          itemRender={undefined}
          onChange={handleFileImport}
          maxCount={1}
          showUploadList={false}
        >
          <TooltipWithIcon title={t('global.import')}>
            <ImportIcon />
          </TooltipWithIcon>
        </Upload>
        <Select
          value={rateType}
          style={{ width: 180 }}
          onChange={(value: RateType) => setRateType(value)}
          options={[
            { label: 'Annual', value: 'annual' },
            { label: 'Quarterly', value: 'quarterly' },
            { label: 'Monthly', value: 'monthly' },
          ]}
        />
      </div>
    );
  };

  const tabItems: ICustomTabs[] = [
    {
      key: '1',
      label: '',
      children: <TableComponent columns={newColumns} dataSource={currencies} />,
      extendedContents: createImportBtn(),
    },
  ];
  return <TabLayout paramName="subTab" items={tabItems} className="tabs-with-border-container" />;
}
